import { FileText, ImageIcon, Paperclip, Star, X } from 'lucide-react';
import { useId, useRef } from 'react';
import { formatBytes } from '../../lib/format.ts';

// ── Star rating ─────────────────────────────────────────────────────

const RATING_WORDS = ['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];

/** 1–5 stars as a radio group, so it works with keyboard and screen readers. */
export function StarRating({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  error?: string;
}) {
  const name = useId();
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">{label}</legend>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <label
            key={n}
            className="cursor-pointer rounded-md p-0.5 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand"
          >
            <input
              type="radio"
              name={name}
              value={n}
              checked={value === n}
              onChange={() => onChange(n)}
              className="sr-only"
              aria-label={`${n} star${n > 1 ? 's' : ''} — ${RATING_WORDS[n]}`}
            />
            <Star
              className={`size-8 transition-colors ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-line hover:text-amber-300'}`}
              aria-hidden
            />
          </label>
        ))}
        <span className="ml-2 text-sm text-muted">{RATING_WORDS[value]}</span>
      </div>
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </fieldset>
  );
}

export function StarDisplay({ value }: { value: number | null }) {
  if (!value) return <span className="text-muted">—</span>;
  return (
    <span className="inline-flex" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`size-4 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-line'}`}
          aria-hidden
        />
      ))}
    </span>
  );
}

// ── Attachments ─────────────────────────────────────────────────────

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

/** Check files in the browser first (the server re-checks the real type). */
export function checkFiles(existing: File[], added: File[]): { files: File[]; error?: string } {
  const files = [...existing];
  for (const f of added) {
    if (!ACCEPT.split(',').includes(f.type))
      return { files, error: `“${f.name}” is not a JPG, PNG, WebP image or a PDF.` };
    if (f.size > MAX_FILE_BYTES) return { files, error: `“${f.name}” is larger than 5 MB.` };
    if (files.length >= MAX_FILES) return { files, error: `You can attach up to ${MAX_FILES} files.` };
    files.push(f);
  }
  return { files };
}

export function AttachmentPicker({
  files,
  onChange,
  error,
  onError,
  compact = false,
  label = 'Attach',
}: {
  files: File[];
  onChange: (files: File[]) => void;
  error?: string;
  onError: (message: string | undefined) => void;
  compact?: boolean;
  /** The compact button's text. */
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  const add = (list: FileList | null) => {
    if (!list) return;
    const result = checkFiles(files, [...list]);
    onChange(result.files);
    onError(result.error);
    if (input.current) input.current.value = '';
  };

  return (
    <div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => add(e.target.files)}
      />
      {compact ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-text"
        >
          <Paperclip className="size-4" aria-hidden />
          {label}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            add(e.dataTransfer.files);
          }}
          className="flex w-full flex-col items-center gap-1 rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center text-sm text-muted transition-colors hover:border-brand hover:bg-brand-soft/40"
        >
          <Paperclip className="size-5" aria-hidden />
          <span>
            <span className="font-medium text-brand">Add a photo or PDF</span> — up to {MAX_FILES} files, 5 MB
            each
          </span>
        </button>
      )}
      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex max-w-full items-center gap-2 rounded-lg border border-line bg-surface-2 py-1 pr-1 pl-2.5 text-sm"
            >
              {f.type === 'application/pdf' ? (
                <FileText className="size-4 shrink-0 text-muted" aria-hidden />
              ) : (
                <ImageIcon className="size-4 shrink-0 text-muted" aria-hidden />
              )}
              <span className="truncate">{f.name}</span>
              <span className="shrink-0 text-muted">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="rounded p-0.5 text-muted hover:bg-surface hover:text-text"
                aria-label={`Remove ${f.name}`}
              >
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
